import { defineComponent as _defineComponent } from 'vue'
import { renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, withModifiers as _withModifiers, withKeys as _withKeys, normalizeClass as _normalizeClass, unref as _unref, createBlock as _createBlock, createCommentVNode as _createCommentVNode, resolveDynamicComponent as _resolveDynamicComponent, mergeProps as _mergeProps, normalizeStyle as _normalizeStyle } from "vue"

const _hoisted_1 = { class: "c-tabs__inner" }
const _hoisted_2 = { class: "c-tabs__nav-wrap" }
const _hoisted_3 = {
  class: "c-tabs__nav",
  role: "tablist",
  "aria-label": "Tabs navigation"
}
const _hoisted_4 = ["id", "aria-selected", "aria-controls", "onClick", "onKeydown"]
const _hoisted_5 = { class: "c-tabs__tab-text" }
const _hoisted_6 = ["id", "aria-labelledby"]

import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { Placeholder } from '@sitecore-jss/sitecore-jss-vue';
import type { ComponentRendering } from '@sitecore-jss/sitecore-jss-vue';
import type { TabsProps, TabsItemFields } from '@/types/components/tabs';
import type { ScItem } from '@/types/sitecore';
import componentFactory from '@/temp/componentFactory';
import utils from '@/utils';

type DynamicComponentItem = {
  uid?: string;
  dataSource?: string;
  id?: string;
  name?: string;
  displayName?: string;
  componentName?: string;
  template?: string;
  templateName?: string;
  fields?: Record<string, unknown> | null;
  placeholders?: Record<string, ComponentRendering[]>;
};

interface NormalizedTab {
  id: string;
  title: string;
  components: DynamicComponentItem[];
}


export default /*@__PURE__*/_defineComponent({
  __name: 'Tabs',
  props: {
    dataSource: {},
    fields: {},
    rendering: {},
    params: {}
  },
  setup(__props: any) {

const props = __props;
const activeIndex = ref(0);
let autoSwitchTimer: ReturnType<typeof setInterval> | null = null;

const rootClass = computed(() => {
  return utils.overall.baseComClass(props.fields, 'c-tabs');
});

const rootStyle = computed(() => {
  return utils.overall.baseComStyle(props.fields);
});
const fields = computed(() => props.fields);
const autoSwitchInterval = computed(() => {
  const seconds = Number(fields.value?.autoSwitchInterval?.value);
  return Number.isFinite(seconds) && seconds > 0 ? seconds * 1000 : 1000;
});

const configuredTabs = computed<ScItem<TabsItemFields>[]>(() => {
  const tabs = utils.sc.getFieldObj(fields.value, 'tabs');
  return Array.isArray(tabs) ? tabs : [];
});

const placeholderRenderings = computed<ComponentRendering[]>(() => {
  const placeholders = props.rendering?.placeholders as
    Record<string, ComponentRendering[]> | undefined;
  const renderings = placeholders?.['jss-tabs'];
  return Array.isArray(renderings) ? renderings : [];
});

const normalizedTabs = computed<NormalizedTab[]>(() => {
  const length = Math.max(configuredTabs.value.length, placeholderRenderings.value.length);

  return Array.from({ length }, (_, index) => {
    const tabItem = configuredTabs.value[index];
    const childRendering = placeholderRenderings.value[index] as any;
    const tabFields = tabItem?.fields;
    const childFields = childRendering?.fields;
    const title =
      tabFields?.title?.value ||
      childFields?.tabTitle?.value ||
      childFields?.title?.value ||
      childRendering?.name ||
      childRendering?.componentName ||
      `Tab ${index + 1}`;

    return {
      id: tabItem?.id || childRendering?.uid || childRendering?.dataSource || `tabs-${index}`,
      title,
      components: (tabFields?.components ?? []).map((componentData) => ({
        ...componentData,
        componentName: getComponentName(componentData),
      })),
    };
  });
});

const activeComponents = computed(() => normalizedTabs.value[activeIndex.value]?.components ?? []);

const hasPlaceholderContent = computed(() => placeholderRenderings.value.length > 0);

const selectedPlaceholderRendering = computed<ComponentRendering>(() => {
  const currentRendering = placeholderRenderings.value[activeIndex.value];

  return {
    ...props.rendering,
    placeholders: {
      ...(props.rendering?.placeholders || {}),
      'jss-tabs': currentRendering ? [currentRendering] : [],
    },
  } as ComponentRendering;
});

const selectedPlaceholderKey = computed(() => {
  const currentRendering = placeholderRenderings.value[activeIndex.value] as any;
  return currentRendering?.uid || currentRendering?.dataSource || `tabs-panel-${activeIndex.value}`;
});

function getComponentName(componentData?: DynamicComponentItem) {
  return `${
    componentData?.componentName ||
    componentData?.templateName ||
    componentData?.template ||
    'ImageTextCard'
  }`.replace(/\s+/g, '');
}

function resolveDynamicComponent(componentData?: DynamicComponentItem) {
  const componentName = getComponentName(componentData);
  return componentFactory(componentName) || componentName;
}

function getDynamicComponentProps(componentData: DynamicComponentItem) {
  return {
    fields: componentData?.fields ?? {},
    rendering: componentData as ComponentRendering,
  };
}

const instanceId = computed(() =>
  String(props.rendering?.uid || props.dataSource || 'tabs').replace(/[^a-zA-Z0-9_-]/g, '')
);

const tabButtonId = (index: number) => `${instanceId.value}-tab-${index}`;
const tabPanelId = (index: number) => `${instanceId.value}-panel-${index}`;

const setActiveIndex = (index: number) => {
  activeIndex.value = index;
  resetAutoSwitch();
};

const focusTab = async (index: number) => {
  const tabCount = normalizedTabs.value.length;
  if (!tabCount) return;

  const nextIndex = (index + tabCount) % tabCount;
  activeIndex.value = nextIndex;
  resetAutoSwitch();
  await nextTick();
  document.getElementById(tabButtonId(nextIndex))?.focus();
};

const clearAutoSwitch = () => {
  if (!autoSwitchTimer) return;
  clearInterval(autoSwitchTimer);
  autoSwitchTimer = null;
};

const startAutoSwitch = () => {
  clearAutoSwitch();
  if (normalizedTabs.value.length <= 1) return;

  autoSwitchTimer = setInterval(() => {
    const tabCount = normalizedTabs.value.length;
    if (tabCount <= 1) {
      clearAutoSwitch();
      return;
    }

    activeIndex.value = (activeIndex.value + 1) % tabCount;
  }, autoSwitchInterval.value);
};

const resetAutoSwitch = () => {
  startAutoSwitch();
};

watch(
  () => normalizedTabs.value.length,
  (tabCount) => {
    if (activeIndex.value >= tabCount) activeIndex.value = 0;
    startAutoSwitch();
  }
);

onMounted(() => {
  startAutoSwitch();
});

onBeforeUnmount(() => {
  clearAutoSwitch();
});

return (_ctx: any,_cache: any) => {
  return (fields.value)
    ? (_openBlock(), _createElementBlock("section", {
        key: 0,
        class: _normalizeClass(rootClass.value),
        style: _normalizeStyle(rootStyle.value)
      }, [
        _createElementVNode("div", _hoisted_1, [
          _createElementVNode("div", _hoisted_2, [
            _createElementVNode("div", _hoisted_3, [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(normalizedTabs.value, (tab, index) => {
                return (_openBlock(), _createElementBlock("button", {
                  id: tabButtonId(index),
                  key: tab.id,
                  class: _normalizeClass(["c-tabs__tab", { 'is-active': activeIndex.value === index }]),
                  type: "button",
                  role: "tab",
                  "aria-selected": activeIndex.value === index,
                  "aria-controls": tabPanelId(index),
                  onClick: ($event: any) => (setActiveIndex(index)),
                  onKeydown: [
                    _withKeys(_withModifiers(($event: any) => (focusTab(index - 1)), ["prevent"]), ["left"]),
                    _withKeys(_withModifiers(($event: any) => (focusTab(index + 1)), ["prevent"]), ["right"]),
                    _cache[0] || (_cache[0] = _withKeys(_withModifiers(($event: any) => (focusTab(0)), ["prevent"]), ["home"])),
                    _cache[1] || (_cache[1] = _withKeys(_withModifiers(($event: any) => (focusTab(normalizedTabs.value.length - 1)), ["prevent"]), ["end"]))
                  ]
                }, [
                  _cache[2] || (_cache[2] = _createElementVNode("span", {
                    class: "c-tabs__tab-bg",
                    "aria-hidden": "true"
                  }, null, -1)),
                  _createElementVNode("span", _hoisted_5, _toDisplayString(tab.title), 1)
                ], 42, _hoisted_4))
              }), 128))
            ])
          ]),
          (hasPlaceholderContent.value || activeComponents.value.length)
            ? (_openBlock(), _createElementBlock("div", {
                key: 0,
                id: tabPanelId(activeIndex.value),
                class: "c-tabs__panel",
                role: "tabpanel",
                "aria-labelledby": tabButtonId(activeIndex.value)
              }, [
                (hasPlaceholderContent.value)
                  ? (_openBlock(), _createBlock(_unref(Placeholder), {
                      key: selectedPlaceholderKey.value,
                      name: "jss-tabs",
                      rendering: selectedPlaceholderRendering.value
                    }, null, 8, ["rendering"]))
                  : (_openBlock(true), _createElementBlock(_Fragment, { key: 1 }, _renderList(activeComponents.value, (componentData, componentIndex) => {
                      return (_openBlock(), _createBlock(_resolveDynamicComponent(resolveDynamicComponent(componentData)), _mergeProps({ ref_for: true }, getDynamicComponentProps(componentData), {
                        key: componentData?.uid ?? componentData?.id ?? `${activeIndex.value}-${componentIndex}`
                      }), null, 16))
                    }), 128))
              ], 8, _hoisted_6))
            : _createCommentVNode("", true)
        ])
      ], 6))
    : _createCommentVNode("", true)
}
}

})